import { Breakpoint } from 'theme/Theme';
import {
  Children,
  Fragment,
  PropsWithChildren,
  ReactElement,
  cloneElement,
  isValidElement,
  useState,
} from 'react';
import { Divider, IconButton, Popover } from 'omakase';
import { IconEllipses } from '@tock/icons';
import { buttonGroup, collapsedChild } from 'components/buttons/collapsibleButtonGroupCssDefs';
import { makeStyles } from '@material-ui/core/styles';
import { useCurrentBreakpoint } from 'hooks/useCurrentBreakpoint';

const useStyles = makeStyles(({ spacing }) => ({
  popover: {
    padding: `${spacing(1)}px ${spacing(2)}px`,
  },
}));

interface CollapsibleButtonGroupProps<P> {
  /**
   * Properties added to children that are not moved to the popover when the children are collapsed.
   */
  expandedChildProps?: P;
  /**
   * Properties added to children that are moved to the popover when the children are collapsed.
   */
  collapsedChildProps?: P;
  /**
   * Number of children to keep expanded (not in the popover) when the screen is at a specific size.
   *
   * Ex:
   *
   * <CollapsibleButtonGroup cutoff={{ xs: 1, sm: 2, md: 3 }}>
   *   <Button>Button #1</Button>
   *   <Button>Button #2</Button> // collapsed when screen size is xsmall, small, or medium
   *   <Button>Button #3</Button> // collapsed when screen size is small or medium
   *   <Button>Button #4</Button> // collapsed when screen size is medium
   * </CollapsibleButtonGroup>
   */
  cutoff?: { [K in Breakpoint]?: number };
}

const CollapsibleButtonGroup = <P extends object>({
  expandedChildProps,
  collapsedChildProps,
  cutoff = {},
  children,
}: PropsWithChildren<CollapsibleButtonGroupProps<P>>) => {
  const [showMoreEl, setShowMoreEl] = useState<HTMLButtonElement | null>(null);

  const styles = useStyles();
  const breakpoint = useCurrentBreakpoint();

  const handleShowMoreClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setShowMoreEl(event.currentTarget);
  };

  const handleShowMoreClose = () => {
    setShowMoreEl(null);
  };

  let validChildren =
    Children.map(children, (child) => {
      return isValidElement(child) && typeof child === 'object' ? (child as ReactElement<P>) : null;
    }) ?? [];

  const currentCutoff = cutoff[breakpoint];

  const collapsedChildren =
    currentCutoff && validChildren.length > currentCutoff
      ? validChildren.splice(currentCutoff).map((child) => cloneElement(child, collapsedChildProps))
      : [];

  if (expandedChildProps && collapsedChildren.length > 0) {
    validChildren = validChildren.map((child) => cloneElement(child, expandedChildProps));
  }

  if (validChildren.length === 0) {
    return null;
  }

  return (
    <div css={buttonGroup}>
      {validChildren}
      {collapsedChildren.length > 0 && (
        <>
          <IconButton
            aria-label="See more"
            size="medium"
            variant="basic"
            data-testid="show-more"
            onClick={handleShowMoreClick}
          >
            <IconEllipses />
          </IconButton>
          <Popover
            anchorEl={showMoreEl}
            open={!!showMoreEl}
            onClose={handleShowMoreClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            PaperProps={{
              className: styles.popover,
            }}
          >
            {collapsedChildren.map((child, idx) => (
              <Fragment key={idx}>
                {idx > 0 && <Divider />}
                <div css={collapsedChild}>{child}</div>
              </Fragment>
            ))}
          </Popover>
        </>
      )}
    </div>
  );
};

export default CollapsibleButtonGroup;
